// ============================================================================
// Rota · contratos V2 (suíte) · 22/10/2026 · packages/contracts/src/v2.ts
// Só adições: arquivo novo + campos OPCIONAIS em interfaces existentes por module augmentation.
// Nenhum tipo, união ou constante da V1 é renomeado ou alterado. index.ts ganha 1 linha:
//   export * from './v2.ts';
// ============================================================================
import type {
  AuditEntry,
  Briefing,
  Cents,
  DataProvider,
  DataSource,
  DateKey,
  Freshness,
  Id,
  IntegrationState,
  KpiFormat,
  KpiStatus,
  KpiValue,
  LatencyClass,
  MetricCategory,
  Ms,
  OwnerTag,
  Page,
  Permission,
  Polarity,
  ProductKind,
  Query,
  QuizQuestionId,
  Rate,
  Role,
  RuleId,
  ScenarioId,
  Severity,
  SimState,
  ThresholdSpec,
  TrackingParams,
  Unsubscribe,
  Viewer,
} from './index.ts';

export const CONTRACT_VERSION_V2 = 2 as const;

// ============================================================================
// 1. SUÍTE: áreas, permissões, funções
// ============================================================================
export type Area = 'painel' | 'automacoes' | 'estudio' | 'palco';

export type SuitePermission =
  | 'area:painel'
  | 'area:automacoes'
  | 'area:estudio'
  | 'ig:read' // métricas do Instagram (agregadas, sem dado pessoal)
  | 'ig:connect' // OAuth, tokens, assinaturas de webhook (exige step-up)
  | 'inbox:read'
  | 'inbox:reply'
  | 'inbox:expert' // expert: fila "Tiago" (quentes)
  | 'inbox:supervise' // dono e sócio: transferem e liberam leads com responsável, criam filas e etiquetas
  | 'automations:write' // rascunho de automação e fluxo
  | 'automations:publish' // ligar em produção (exige step-up)
  | 'automations:pause' // pausa de emergência de uma automação
  | 'messages:write'
  | 'messages:approve' // banco de variações
  | 'safety:write' // limites de segurança
  | 'kill_switch' // "Desligar tudo"
  | 'backend:read'
  | 'backend:work' // consultorias de R$ 100 mil
  | 'plan:write' // plano de 90 dias e furos
  | 'audit:read'
  | 'security:manage';

export const ROLE_SUITE_PERMISSIONS: Record<Role, readonly SuitePermission[]> = {
  owner: [
    'area:painel',
    'area:automacoes',
    'area:estudio',
    'ig:read',
    'ig:connect',
    'inbox:read',
    'inbox:reply',
    'inbox:expert',
    'inbox:supervise',
    'automations:write',
    'automations:publish',
    'automations:pause',
    'messages:write',
    'messages:approve',
    'safety:write',
    'kill_switch',
    'backend:read',
    'backend:work',
    'plan:write',
    'audit:read',
    'security:manage',
  ],
  partner: [
    'area:painel',
    'area:automacoes',
    'area:estudio',
    'ig:read',
    // Sócio acompanha o atendimento: passa e libera leads (só dono e sócio fazem isso).
    'inbox:read',
    'inbox:reply',
    'inbox:expert',
    'inbox:supervise',
    'kill_switch',
    'backend:read',
    'plan:write',
  ],
  traffic: ['area:painel', 'area:automacoes', 'area:estudio', 'ig:read'],
  creative: ['area:painel', 'area:automacoes', 'area:estudio', 'ig:read', 'messages:write'],
  // Atendimento: só o bate-papo das Automações (sem Painel, Estúdio, métricas nem faturamento).
  support: ['area:automacoes', 'inbox:read', 'inbox:reply'],
  expert: [
    'area:painel',
    'area:automacoes',
    'area:estudio',
    'ig:read',
    'inbox:read',
    'inbox:reply',
    'inbox:expert',
    'automations:pause',
    'messages:approve',
    'backend:read',
    'backend:work',
  ],
  tech: ['area:painel', 'area:automacoes', 'ig:read', 'ig:connect', 'automations:pause', 'safety:write'],
  tv: [],
};

/** Função de produção no Estúdio. Independe do Role; uma pessoa pode ter várias. */
export type StudioFunction = 'expert' | 'copy' | 'editor' | 'social' | 'traffic' | 'producer' | 'compliance';
export type StudioPermission =
  | 'studio:read'
  | 'studio:write'
  | 'studio:assign'
  | 'script:write'
  | 'hook:write'
  | 'approve:truth'
  | 'approve:compliance'
  | 'approve:final_cut'
  | 'asset:upload'
  | 'asset:review'
  | 'publish:organic'
  | 'ads:upload'
  | 'review:run'
  | 'studies:write';
export const STUDIO_FUNCTION_PERMISSIONS: Record<StudioFunction, readonly StudioPermission[]> = {
  expert: ['studio:read', 'studio:write', 'approve:truth', 'asset:review', 'studies:write'],
  copy: ['studio:read', 'studio:write', 'script:write', 'hook:write', 'asset:review', 'studies:write'],
  editor: ['studio:read', 'studio:write', 'asset:upload', 'asset:review'],
  social: ['studio:read', 'studio:write', 'hook:write', 'asset:review', 'publish:organic', 'studies:write'],
  traffic: ['studio:read', 'asset:review', 'ads:upload', 'review:run'],
  producer: ['studio:read', 'studio:write', 'studio:assign', 'review:run', 'studies:write'],
  compliance: ['studio:read', 'approve:compliance', 'approve:final_cut', 'asset:review'],
};
export const DEFAULT_FUNCTIONS_BY_ROLE: Record<Role, readonly StudioFunction[]> = {
  owner: ['producer', 'compliance'],
  partner: ['producer'],
  traffic: ['traffic'],
  creative: ['copy', 'editor'],
  support: ['social'],
  expert: ['expert'],
  tech: [],
  tv: [],
};

/** Sessão: o Viewer da V1 continua sendo a identidade do Painel. */
export interface SessionInfo {
  viewer: Viewer; // role principal + permissions da V1
  email: string;
  functions: StudioFunction[];
  suitePermissions: readonly SuitePermission[];
  studioPermissions: readonly StudioPermission[];
  areas: Area[]; // derivado de area:*
  home: string; // rota inicial (2.4)
  mfa: { enrolled: boolean; required: boolean };
  expiresAt: Ms;
  stepUpUntil: Ms | null;
  remembered: boolean;
}

// ============================================================================
// 2. AUTH (mock local na V2; better-auth na fase 2) — mesma interface
// ============================================================================
export type SignInResult =
  | { status: 'ok'; session: SessionInfo }
  | { status: 'totp_required'; challengeId: Id }
  | { status: 'invalid' }
  | { status: 'throttled'; retryAt: Ms }
  | { status: 'setup_required' }; // mock: nenhum dono criado neste navegador
export interface TeamMember {
  id: Id;
  name: string;
  initials: string;
  email: string;
  role: Role;
  functions: StudioFunction[];
  status: 'active' | 'invited' | 'disabled';
  lastSeenAt: Ms | null;
  mfaEnrolled: boolean;
}
export interface InviteResult {
  memberId: Id;
  link: string;
  expiresAt: Ms;
  /** O convite também saiu por e-mail para a pessoa (só com o e-mail dos avisos pronto). */
  emailed?: boolean;
} // link com token no fragmento '#t='
export type SuiteAuditAction =
  | AuditEntry['action']
  | 'login_ok'
  | 'login_failed'
  | 'logout'
  | 'mfa'
  | 'invite'
  | 'role_change'
  | 'password_change'
  | 'session_revoked'
  | 'ig_connect'
  | 'ig_token_refresh'
  | 'automation_publish'
  | 'automation_pause'
  | 'kill_switch'
  | 'human_message'
  | 'backend_change'
  | 'studio_approval';
export interface SuiteAuditEntry {
  id: Id;
  at: Ms;
  actorId: Id | 'system';
  action: SuiteAuditAction;
  area: Area | 'suite';
  detail: string;
  prevHash?: string;
  hash?: string;
}
export interface AuthProvider {
  readonly kind: 'mock' | 'api';
  session(): Promise<SessionInfo | null>;
  setupOwner?(i: { name: string; email: string; password: string }): Promise<SessionInfo>; // só mock
  signIn(i: { email: string; password: string; remember: boolean }): Promise<SignInResult>;
  verifyTotp(challengeId: Id, code: string): Promise<SessionInfo>;
  stepUp(i: { password?: string; totp?: string }): Promise<SessionInfo>;
  signOut(): Promise<void>;
  requestPasswordReset(email: string): Promise<void>; // sempre resolve (não revela se existe)
  resetPassword(token: string, password: string): Promise<void>;
  acceptInvite(token: string, password: string, name?: string): Promise<SessionInfo>;
  enrollTotp?(): Promise<{ otpauthUrl: string; recoveryCodes: string[] }>;
  team?: {
    list(): Promise<TeamMember[]>;
    invite(i: {
      email: string;
      name: string;
      role: Role;
      functions: StudioFunction[];
    }): Promise<InviteResult>;
    update(
      id: Id,
      patch: { role?: Role; functions?: StudioFunction[]; status?: 'active' | 'disabled' },
    ): Promise<TeamMember>;
    revokeSessions(id: Id): Promise<void>;
    audit(q: { cursor?: string; limit: number; area?: Area | 'suite' }): Promise<Page<SuiteAuditEntry>>;
  };
}

// ============================================================================
// 3. FONTES E FRESCOR ESTENDIDOS (sem mexer em DataSource/Freshness da V1)
// ============================================================================
export type SuiteDataSource = DataSource | 'instagram' | 'redirector' | 'whatsapp' | 'manual' | 'studio';
export type SuiteLatency = LatencyClass | 'expiring_24h';
export interface SuiteFreshness extends Omit<Freshness, 'source' | 'latency'> {
  source: SuiteDataSource;
  latency: SuiteLatency;
  confirmed?: boolean; // insights IG: false até D+3 (podem mudar)
}
/** Valor com origem: null = a API devolveu vazio (nunca é zero). */
export interface Sourced<T> {
  value: T | null;
  freshness: SuiteFreshness;
}

// ============================================================================
// 4. ÁREA A · PAINEL V2
// ============================================================================
export type SalesChannel = 'meta_ads' | 'ig_bio' | 'ig_dm' | 'ig_story' | 'ig_outro' | 'sem_origem';
export type SuiteProductKind = ProductKind | 'step' | 'backend';
export type ChannelView = 'ads' | 'organic' | 'all';

/** Ajustes › Economia (substitui as derivações de 5.1 do SPEC para a V2). */
export interface EconomicsV2 {
  ourSharePct: Rate; // 0,4044 (divisão após a taxa; confirmar coprodução)
  adSpendSharePct: Rate; // 1,0 = a operação paga todo o tráfego (confirmar); base de ROAS do funil, CPA e teto
  netFront: Cents;
  netUpsell: Cents; // 10831 · 25553 (entrada direta, conferida contra ourSharePct)
  step: { enabled: boolean; price: Cents; net: Cents | null; take: Rate | null }; // degrau (mês 2)
  backend: { price: Cents; netPct: Rate | null; countInBuyerValue: 'never' | 'labelled' };
  upsellTakeOverride: Rate | null; // null = medido (u)
  revenueTaxBasis: 'own_commission' | 'gross';
  cpaCeilingBasis: 'receita_no_ato' | 'contribuicao';
  testBudgetFactor: Rate; // 0,5 × valor do comprador por dia
  testDays: number; // 2
  bestOfWeekRange: [Rate, Rate]; // [0,75; 1,0] × valor do comprador
  dormantBudget: Cents; // 3000
  scaleStepRange: [Rate, Rate]; // [0,20; 0,30]
  vsl: {
    durationSec: number;
    pitchSec: number;
    minute1Sec: 60;
    blocks: {
      id: 'lead' | 'problema' | 'historia' | 'solucao' | 'oferta' | 'fechamento';
      fromSec: number;
      toSec: number;
    }[];
  };
  upsellVsl: { durationSec: number; pitchSec: number };
  frozen: { at: Ms; buyerValue: Cents; testBudgetDay: Cents; ceiling: Cents; u: Rate } | null; // congelado até a próxima segunda (R57)
}
export interface BuyerValue {
  atSale: Cents; // netFront + u·netUpsell + d·netStep
  withBackend: { cents: Cents; sample: number; label: string } | null; // sempre rotulado
  u: { rate: Rate; buyers: number; takers: number; ci90: [Rate, Rate] };
  d: { rate: Rate; buyers: number; takers: number } | null;
  b: { closed: number; frontBuyers: number; perFrontBuyerGross: Cents; ci90: [Rate, Rate] | null };
  upsellContribution: Rate; // u·netUpsell ÷ netFront
  ceilingAtSale: Cents;
  ceilingReal: Cents;
  testBudgetDay: Cents;
  bestOfWeekDay: [Cents, Cents];
  frozenUntil: Ms | null;
}
export type KpiIdV2 =
  | 'buyerValue'
  | 'buyerValueBackend'
  | 'upsellContribution'
  | 'roasFunnel'
  | 'roasFunnelBackend'
  | 'cpaMeta'
  | 'ltvBuyer'
  | 'backendPerFrontBuyer'
  | 'vslMinute1'
  | 'pitchConversion'
  | 'ctaClickCoverage'
  | 'offerShare'
  | 'upsellPlayRate'
  | 'upsellMinute1'
  | 'upsellPitchRetention'
  | 'adPlayRate'
  | 'costPerVisit'
  | 'costPerPitch'
  | 'utmContentCoverage'
  | 'trackedClicks'
  | 'organicSales'
  | 'unattributedShare'
  | 'backendOpen'
  | 'backendValueAtStake'
  | 'backendCloseRate'
  | 'testBudgetDay';
export interface KpiDefV2 {
  id: KpiIdV2;
  label: string;
  short?: string;
  help: string;
  formula: string;
  sources: SuiteDataSource[];
  format: KpiFormat;
  polarity: Polarity;
  thresholds: ThresholdSpec;
  minSample?: { unit: string; n: number };
  estimated?: boolean;
  requires?: Permission | SuitePermission;
  category: MetricCategory | 'backend' | 'canais';
}
export interface KpiValueV2 extends Omit<KpiValue, 'id' | 'freshness'> {
  id: KpiIdV2;
  freshness: SuiteFreshness[];
}

export interface ChannelRow {
  channel: SalesChannel;
  clicks: number | null;
  quizCompletes: number | null;
  vslPlays: number;
  frontSales: number;
  upsellSales: number;
  backendClosed: number;
  revenueNet: Cents;
  backendGross: Cents;
  spend: Cents;
  ltvPerBuyer: Cents | null;
  share: Rate;
}
export interface PostRow {
  // FUN-07: venda por post (só via DM/link com id do post)
  mediaId: Id;
  label: string;
  thumbnailUrl: string | null;
  publishedAt: Ms;
  clicks: number;
  frontSales: number;
  upsellSales: number;
  backendClosed: number;
  revenueNet: Cents;
}
export interface ChannelReport {
  rows: ChannelRow[];
  posts: PostRow[];
  coverage: Rate;
  freshness: SuiteFreshness[];
  /** Sem `backend:read`: consultorias zeradas e `ltvPerBuyer` só no ato (ADR-018, rev. 23/10). */
  backendRestricted?: boolean;
}

// ---- Backend: consultoria de R$ 100 mil ----
export type BackendStage =
  | 'B0_comprou_20x'
  | 'B1_primeiro_contato'
  | 'B2_diagnostico'
  | 'B3_acompanhamento'
  | 'B4_qualificacao'
  | 'B5_proposta'
  | 'B6_decisao'
  | 'B7_resultado';
export type BackendOutcome = 'won' | 'lost' | 'postponed' | 'to_step';
/**
 * Meta de volume das consultorias (SPEC-v2 topo item 4; ADR-017): consultorias novas por dia e por mês.
 * É meta, nunca teto: a regra de ritmo (R62) só informa e sugere expandir o time.
 */
export interface ConsultingCapacitySetting {
  perDay: number;
  perMonth: number;
}
/** Prazos e probabilidades do pipeline (V2 3.4; ADR-017). Campos ausentes = padrão da SPEC. */
export interface BackendPipelineSetting {
  /** B0 → B1 em horário comercial (padrão 60). */
  firstContactMin?: number;
  /** Horário comercial do 1º contato, 'HH:MM' em São Paulo (padrão 09:00–21:00). */
  businessHours?: { start: string; end: string };
  /** Prazo de cada etapa em dias desde a compra do 20X (B2 3 · B3/B4 14 · B5 21 · B6 35). */
  stageDueDays?: Partial<Record<BackendStage, number>>;
  /** p(etapa) `[E]` do valor em jogo (B1–B3 0,30 · B4 0,50 · B5 0,60 · B6 0,75). */
  probabilities?: Partial<Record<BackendStage, Rate>>;
  /** R55: dias sem movimento para "oportunidade parada" (padrão 5). */
  staleDays?: number;
}
export interface BackendEvent {
  at: Ms;
  by: Id | 'system';
  kind: 'stage' | 'note' | 'message_logged' | 'value' | 'payment';
  from?: BackendStage;
  to?: BackendStage;
  text?: string;
  cents?: Cents;
}
export interface BackendDeal {
  id: Id;
  buyerKey: string; // hash de e-mail/CPF
  customer: { displayName: string; phoneMasked: string | null } | null; // pii:read ou backend:work (do próprio expert)
  frontOrderId: Id;
  upsellOrderId: Id | null;
  origin: { channel: SalesChannel; adName?: string; mediaId?: Id; label: string };
  stage: BackendStage;
  stageSince: Ms;
  outcome?: BackendOutcome;
  lostReason?: string;
  returnAt?: Ms;
  ownerId: Id;
  probability: Rate; // p(etapa), [E]
  proposalCents: Cents | null;
  contractedCents: Cents | null;
  receivedCents: Cents;
  installments?: { dueAt: Ms; cents: Cents; paidAt: Ms | null }[];
  nextAction: { label: string; dueAt: Ms } | null;
  program?: { startedAt: Ms; endsAt: Ms; week: 1 | 2 | 3 | 4; interactionDays: number }; // 30 dias do 20X, trilha paralela às etapas
  quizSummary?: { questionId: QuizQuestionId; label: string }[];
  events: BackendEvent[];
  createdAt: Ms;
  updatedAt: Ms;
}
export type BackendCommand =
  | { kind: 'move'; dealId: Id; to: BackendStage; note?: string }
  | { kind: 'note'; dealId: Id; text: string }
  | { kind: 'log_message'; dealId: Id; step: string } // marca mensagem do funil do WhatsApp como enviada (manual)
  | { kind: 'set_value'; dealId: Id; proposalCents?: Cents; contractedCents?: Cents }
  | { kind: 'payment'; dealId: Id; cents: Cents; at: Ms }
  | { kind: 'close'; dealId: Id; outcome: BackendOutcome; reason?: string; returnAt?: Ms }
  | { kind: 'create_manual'; frontOrderId: Id; upsellOrderId?: Id; note?: string };
export interface BackendReport {
  open: number;
  valueAtStake: Cents;
  ceilingAtStake: Cents;
  closedCount: number;
  closedCents: Cents;
  receivedCents: Cents;
  closeRateOnProposals: { rate: Rate; n: number; ci90: [Rate, Rate] } | null;
  medianCycleDays: number | null;
  overdue: number;
  funnel: {
    stage: BackendStage | 'front' | 'upsell';
    count: number;
    rateFromPrev: Rate | null;
    ci90: [Rate, Rate] | null;
  }[];
  byOrigin: {
    label: string;
    frontBuyers: number;
    upsellBuyers: number;
    proposals: number;
    won: number;
    cents: Cents;
  }[];
  deals: BackendDeal[];
  sampleNote: string; // "probabilidades estimadas; histórico de 3 casos"
  /** Consultorias pagas do mês corrente (fuso de São Paulo), independente do período escolhido. Opcional. */
  monthToDate?: { count: number; cents: Cents };
  /** Conversas do WhatsApp do 20X (furo 03): total e por canal de origem. Opcional (ADR-016). */
  conversations?: { overall: BackendConversationStats; byChannel: BackendConversationStats[] };
  /** Consultorias do mês × meta de volume (3/dia, 90/mês). Informativo, nunca teto (ADR-016). */
  consulting?: BackendConsultingPace;
  /** Segunda chance (furo 04): quem nunca recebe outra oferta e a meta de 1 em 10 em conversa (ADR-016). */
  secondChance?: BackendSecondChance;
}
/** Métricas da conversa do WhatsApp do 20X por origem (registro manual; V2 3.4, furo 03). */
export interface BackendConversationStats {
  key: string; // 'all' ou o SalesChannel
  label: string;
  n: number;
  firstMessageMedianMin: number | null; // compra → 1ª mensagem (mediana)
  firstMessageOnTime: Rate | null; // dentro do prazo de B1 (60 min em horário comercial)
  withoutFirstMessage: number;
  proposals: number;
  proposalRate: Rate | null;
  won: number;
  closeRate: Rate | null;
  closeRateCi90: [Rate, Rate] | null;
  open: number;
  smallSample: boolean; // < 10 conversas
}
/** "Consultorias do mês vs. meta de 90" e "ritmo diário vs. 3/dia" (topo do SPEC-v2, item 4). */
export interface BackendConsultingPace {
  perDayGoal: number;
  monthGoal: number;
  monthToDate: number;
  expectedToDate: number;
  perDay: number;
  perDaySource: 'projected' | 'last7d' | 'month';
  last7d: number;
  projectedMonth: number;
  dayOfMonth: number;
  daysInMonth: number;
  status: 'below' | 'on_track' | 'above'; // 'above' só sugere expandir o time
}
export interface BackendSecondChance {
  frontBuyers: number;
  upsellBuyers: number;
  withoutSecondOffer: number; // 40 de 43 hoje
  inConversation: number;
  conversationRate: Rate | null;
  goal: Rate; // 0,1 = 1 em cada 10 compradores do front em conversa com o Tiago
  invitedRate: Rate | null;
  attendedRate: Rate | null;
  upsellFromClassRate: Rate | null;
}
export interface LtvRow {
  key: string;
  label: string;
  level: 'ad' | 'post' | 'channel' | 'cohort';
  buyers: number;
  frontNet: Cents;
  upsellNet: Cents;
  stepNet: Cents;
  backendGross: Cents;
  refunds: Cents;
  ltvPerBuyer: Cents;
  roasFunnel: number | null;
  roasFunnelBackend: number | null;
}

// ---- Fábrica de criativos ----
export type TestPhase =
  | 'teste_d1'
  | 'teste_d2'
  | 'teste_d3'
  | 'validado'
  | 'melhores_semana'
  | 'escala'
  | 'adormecida'
  | 'cortado';
export interface AdNameParts {
  raw: string;
  seq: number | null;
  personaCode: string | null;
  hookCode: string | null;
  formatCode: string | null;
  version: number | null;
  valid: boolean;
} // C031_PAIS_G2-PARTO_POD_v1
export interface TestLaneItem {
  adId: Id;
  name: string;
  parts: AdNameParts;
  phase: TestPhase;
  day: 1 | 2 | 3;
  spend: Cents;
  budget: Cents;
  frontSales: number;
  playRate: Rate | null;
  hookRate: Rate | null;
  verdict: 'scale' | 'extend_1d' | 'kill' | 'pending';
  bankHook: boolean;
  decisionId?: Id;
}
export interface SliceRow {
  personaCode: string;
  creatives: number;
  spend: Cents;
  frontSales: number;
  upsellSales: number;
  backendClosed: number;
  hookRateAvg: Rate | null;
  cpc7d: Cents | null;
  ctr7d: Rate | null;
  frequency7d: number | null;
  costPerPitch: Cents | null;
  saturation: 'ok' | 'saturating' | 'insufficient';
  decisionId?: Id;
}

// ---- Plano de 90 dias, furos, maturidade ----
export type PlanPhaseId = 'enxergar' | 'consertar_20x' | 'vsl_leads' | 'degrau' | 'escalar';
export interface PlanGoal {
  label: string;
  kpi?: KpiIdV2 | string;
  target: number;
  actual: number | null;
  format: KpiFormat;
  status: KpiStatus | 'done';
}
export interface PlanPhase {
  id: PlanPhaseId;
  title: string;
  from: DateKey;
  to: DateKey;
  current: boolean;
  goals: PlanGoal[];
}
export type PlanTaskStatus = 'todo' | 'doing' | 'done' | 'blocked';
export interface PlanTask {
  id: Id;
  phaseId: PlanPhaseId;
  title: string;
  ownerTag: OwnerTag | 'CP' | 'ED';
  ownerId: Id | null;
  dueOn: DateKey;
  status: PlanTaskStatus;
  evidence?: string;
  furoIds: string[];
  route?: string;
}
export type FuroId = `F${'01' | '02' | '03' | '04' | '05' | '06' | '07' | '08' | '09' | '10' | '11' | '12'}`;
export interface Furo {
  id: FuroId;
  title: string;
  severity: 'critico' | 'alto' | 'medio';
  seeing: string;
  measure: {
    kpi?: KpiIdV2 | string;
    label: string;
    target: string;
    actual: string | null;
    status: KpiStatus;
  };
  phaseId: PlanPhaseId;
  ownerTag: OwnerTag;
  status: 'aberto' | 'em_andamento' | 'corrigido' | 'medido';
  route: string;
}
export interface MaturityScore {
  area: string;
  score: 0 | 1 | 2 | 3 | 4 | 5;
  at: DateKey;
  note?: string;
}
export interface PlanReport {
  phases: PlanPhase[];
  tasks: PlanTask[];
  furos: Furo[];
  maturity: MaturityScore[];
  maturityHistory: { at: DateKey; total: number }[];
  review: { weekOf: DateKey; status: 'pending' | 'done'; route: string; closedAt?: Ms };
}
export type PlanCommand =
  | { kind: 'task'; id: Id; status: PlanTaskStatus; evidence?: string }
  | { kind: 'furo'; id: FuroId; status: Furo['status'] }
  | { kind: 'maturity'; area: string; score: MaturityScore['score']; note?: string };

// ---- Links rastreados (TRK-06) e redirecionador ----
export type TrackedPlacement = 'bio' | 'story_sticker' | 'dm_link' | 'post_cta' | 'other';
export interface TrackedLink {
  id: Id;
  slug: string; // 'bio' | 's/<storyId>' | 'p/<shortcode>' | 'a/<automationSlug>'
  shortUrl: string; // https://go.zeroeua.com.br/<slug>
  destination: 'quiz' | 'vsl'; // bio e story: 'vsl' por padrão (funil real); DM: 'quiz'
  placement: TrackedPlacement;
  utm: Required<Pick<TrackingParams, 'utm_source' | 'utm_medium' | 'utm_campaign'>> &
    Pick<TrackingParams, 'utm_content' | 'utm_term'>;
  mediaId?: Id;
  automationId?: Id;
  createdAt: Ms;
  active: boolean;
  clicks7d: number;
}
export interface RedirectClick {
  clickId: Id;
  at: Ms;
  linkId: Id;
  placement: TrackedPlacement;
  mediaId?: Id;
  country?: string;
  device?: 'mobile' | 'desktop' | 'tablet';
  inAppBrowser: boolean;
}

// ---- Funil V2 por canal, porta do orgânico, perfil do quiz e saúde da medição (ADR-018) ----
/** Etapas do funil V2 (V2 3.2): anúncio → aula, orgânico com o ramo via quiz, até a consultoria. */
export type FunnelStageIdV2 =
  | 'impressions'
  | 'clicks'
  | 'quiz_start'
  | 'quiz_complete'
  | 'vsl_visit'
  | 'vsl_play'
  | 'minute1'
  | 'pitch'
  | 'cta_click'
  | 'checkout'
  | 'front'
  | 'upsell'
  | 'b1_contact'
  | 'b5_proposal'
  | 'b7_won';
export interface FunnelStageV2 {
  id: FunnelStageIdV2;
  /** null = a etapa não é medida hoje (ex.: clique no botão sem o furo 01 fechado). */
  count: number | null;
  source: SuiteDataSource;
  /** Etapa só do ramo via quiz do orgânico (bio → quiz e direct → quiz). */
  branch?: 'via_quiz';
  estimated?: boolean;
}
export type OrganicBranchId = 'bio_quiz' | 'bio_vsl' | 'dm_quiz' | 'story_vsl';
export interface OrganicBranchV2 {
  id: OrganicBranchId;
  clicks: number;
  clickShare: Rate; // dos cliques rastreados do orgânico
  quizCompletes: number | null; // null nos ramos diretos
  vslVisits: number;
  frontSales: number;
  upsellSales: number;
  backendWon: number;
  backendNegotiating: number;
}
export type QuizMomentV2 = 'pronto' | 'planejando' | 'explorando' | 'sem_quiz';
export interface QuizProfileRowV2 {
  moment: QuizMomentV2;
  label: string;
  frontBuyers: number;
  upsellBuyers: number;
  upsellRate: Rate | null;
  upsellCi90: [Rate, Rate] | null;
  proposals: number;
  backendWon: number;
  backendRate: Rate | null;
  smallSample: boolean;
}
/** Comprador do 20X com a origem e o código de perfil do quiz (sem dados pessoais; furo 09). */
export interface UpsellBuyerOriginV2 {
  orderId: Id;
  at: Ms;
  channel: SalesChannel;
  branch: 'via_quiz' | 'direto' | null;
  originLabel: string;
  /** Código `p1-…` levado ao checkout; null sem quiz ou com o furo 09 aberto. */
  quizProfile: string | null;
  profileSummary: string | null;
  moment: QuizMomentV2 | null;
  backend: 'won' | 'negotiating' | 'open' | 'lost' | 'postponed' | null;
  /** Deal do backend (link para `/backend/:id`); ausente sem `backend:read`. ADR-018 (rev. 23/10). */
  dealId?: Id;
}
export type MeasurementEventId =
  | 'page_view'
  | 'initiate_checkout'
  | 'offer_unlocked'
  | 'purchase_value'
  | 'consulting_value'
  | 'utm_ads'
  | 'utm_bio'
  | 'sck_checkout'
  | 'quiz_profile';
export interface MeasurementEventV2 {
  id: MeasurementEventId;
  status: 'ok' | 'partial' | 'missing';
  /** Cobertura medida quando existe (ex.: vendas de anúncio com utm_content ÷ vendas de anúncio). */
  coverage?: { n: number; of: number };
}
export interface FunnelV2Report {
  view: ChannelView;
  from: DateKey;
  to: DateKey;
  stages: FunnelStageV2[];
  /** Ramos do orgânico no mesmo recorte (sempre os quatro, para a porta do orgânico). */
  branches: OrganicBranchV2[];
  /** Conversão visita da aula → compra com e sem quiz (orgânico; mínimo de 300 visitas por lado). */
  withWithoutQuiz: {
    withQuiz: { visits: number; sales: number; conv: Rate | null };
    withoutQuiz: { visits: number; sales: number; conv: Rate | null };
    diff: number | null;
    pValue: number | null;
    verdict: 'with_better' | 'without_better' | 'inconclusive' | 'insufficient';
    minVisits: number;
  };
  /** Coorte inteira do histórico (desde `since`): a amostra é pequena demais para recortar por período. */
  quizProfile: {
    since: DateKey;
    salesViaQuiz: number;
    withCode: number;
    coverage: Rate | null;
    /** Por momento do perfil; só com código (vendas via quiz sem código ficam fora e contadas acima). */
    rows: QuizProfileRowV2[];
    upsellBuyers: UpsellBuyerOriginV2[];
  };
  /** Coorte inteira do histórico (desde `since`). */
  measurement: {
    since: DateKey;
    ctaTracked: boolean;
    ctaClicks: number;
    frontSales: number;
    salesWithClick: number;
    metaSales: number;
    metaWithUtmContent: number;
    events: MeasurementEventV2[];
  };
  freshness: SuiteFreshness[];
  /** Deals do recorte em negociação (B5/B6 abertos): "2 de 3 · 1 em negociação". ADR-018 (rev. 23/10). */
  backendNegotiating?: number;
  /**
   * Tabela do `FUN-07` no MESMO recorte e com a MESMA agregação das etapas (cliques, quiz, plays e
   * cursos batem com o `FUN-01`). ADR-018 (rev. 23/10).
   */
  channels?: { rows: ChannelRow[]; coverage: Rate };
  /** Sem `backend:read`: etapas B*, consultorias e valor com consultoria foram retirados no worker. */
  backendRestricted?: boolean;
}

/** Um dia de São Paulo da automação do Instagram (Hoje, dono 29/10/2026). Dinheiro em centavos de real. */
export interface AutomationDay {
  /** AAAA-MM-DD (São Paulo). */
  day: string;
  /** Meia-noite de SP do dia. */
  start: Ms;
  /** Pedidos pagos da Kiwify atribuídos à automação (marcador do contato ou link da automação). */
  sales: number;
  /** Valor do produto dessas vendas (sem os juros do parcelamento). */
  revenueCents: Cents;
  /** Custo da IA do direct e dos comentários do Instagram, em dólar. */
  costUsd: number;
  /** O mesmo custo em real como o cartão cobra (PTAX do dia × spread × IOF). */
  costCents: Cents;
  profitCents: Cents;
}

/** Resumo da automação do Instagram no período (`GET /api/painel/automacao-resumo`). */
export interface AutomationSummary {
  generatedAt: Ms;
  range: { from: Ms; to: Ms };
  /** Do dia mais recente ao mais antigo. */
  days: AutomationDay[];
  totals: Omit<AutomationDay, 'day' | 'start'>;
  /** Crédito da IA no mês da cobrança da Anthropic (mês UTC): limite − gasto do mês, em dólar. */
  credit: {
    billingMonth: string;
    limitUsd: number;
    spentUsd: number;
    remainingUsd: number;
    /** Freio: acima de brakeUsd no mês, a automação troca para modelos mais baratos. */
    brake: { on: boolean; brakeUsd: number };
  };
}

/** Extensão do provider do Painel: todos opcionais; a UI testa a existência. */
export interface PainelProviderV2 extends DataProvider {
  /** Vendas, faturamento, custo da IA e lucro da automação do Instagram por dia, e o crédito da IA. */
  getAutomationSummary?(q: Query): Promise<AutomationSummary>;
  /** FUN-01 por canal, porta do orgânico (QUI-06/07), perfil do quiz (PER-05) e medição (VSL-09). ADR-018. */
  getFunnelV2?(q: Query & { channel?: ChannelView }): Promise<FunnelV2Report>;
  getEconomics?(q: Query): Promise<{
    settings: EconomicsV2;
    value: BuyerValue;
    scenarios: { label: string; u: Rate; value: BuyerValue }[];
  }>;
  getKpisV2?(
    q: Query & { ids: KpiIdV2[]; channel?: ChannelView },
  ): Promise<Partial<Record<KpiIdV2, KpiValueV2>>>;
  getChannels?(q: Query): Promise<ChannelReport>;
  getBackend?(q: Query): Promise<BackendReport>;
  backend?(cmd: BackendCommand): Promise<BackendDeal>;
  getLtv?(q: Query & { level: LtvRow['level'] }): Promise<LtvRow[]>;
  getTestLane?(q: Query): Promise<TestLaneItem[]>;
  getSlices?(q: Query): Promise<SliceRow[]>;
  getPlan?(): Promise<PlanReport>;
  plan?(cmd: PlanCommand): Promise<PlanReport>;
  listTrackedLinks?(): Promise<TrackedLink[]>;
  upsertTrackedLink?(
    l: Omit<TrackedLink, 'id' | 'createdAt' | 'clicks7d' | 'shortUrl'>,
  ): Promise<TrackedLink>;
  updateEconomics?(patch: Partial<EconomicsV2>): Promise<EconomicsV2>;
}

// ============================================================================
// 5. ÁREA B · INSTAGRAM (dados) — Facebook Login for Business (ADR-011)
// ============================================================================
export type IgProductType = 'FEED' | 'REELS' | 'STORY' | 'AD';
export interface IgAccount {
  igUserId: Id;
  username: string;
  name: string;
  profilePictureUrl: string | null;
  biography: string;
  website: string;
  loginMode: 'facebook_login';
  pageId: Id;
  scopes: string[];
  tokenExpiresAt: Ms | null; // token de Página: null = não expira
  webhookFields: string[];
  isPrimaryReceiver: boolean;
  lastWebhookAt: Ms | null;
  state: IntegrationState;
}
export interface IgAccountSnapshot {
  takenAt: Ms;
  followers: number;
  follows: number;
  mediaCount: number;
} // a cada 10 min
export interface IgDailyStats {
  // delayed_48h, 1 linha por dia; reconsultada até D+3
  date: DateKey;
  reach: Sourced<number>;
  views: Sourced<number>;
  accountsEngaged: Sourced<number>;
  totalInteractions: Sourced<number>;
  likes: Sourced<number>;
  comments: Sourced<number>;
  saves: Sourced<number>;
  shares: Sourced<number>;
  reposts: Sourced<number>;
  storyReplies: Sourced<number>;
  follows: Sourced<number>;
  unfollows: Sourced<number>;
  profileLinksTaps: Sourced<number>; // botões de contato. NÃO é o link da bio
  reachByFollowType?: Partial<Record<'FOLLOWER' | 'NON_FOLLOWER', number>>;
  reachByProduct?: Partial<Record<IgProductType, number>>;
  followersNet: number; // dos snapshots próprios
}
export interface IgMedia {
  mediaId: Id;
  shortcode: string;
  permalink: string;
  mediaType: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  productType: IgProductType;
  caption: string;
  publishedAt: Ms;
  thumbnailUrl: string | null;
  likeCount: number;
  commentsCount: number;
  storyExpiresAt?: Ms;
  contentItemId?: Id; // ligação com o Estúdio
  automationIds: Id[];
  trackingSlug?: string;
}
export interface IgMediaInsights {
  // delayed_48h; story = expiring_24h
  mediaId: Id;
  freshness: SuiteFreshness;
  views?: number;
  reach?: number;
  likes?: number;
  comments?: number;
  saved?: number;
  shares?: number;
  reposts?: number;
  totalInteractions?: number;
  reels?: { avgWatchTimeMs?: number; skipRate3s?: Rate }; // skip rate: proxy do gancho no orgânico
  profileVisits?: number;
  follows?: number; // FEED e STORY; Reels não têm
  bioLinkClicked?: number; // profile_activity BIO_LINK_CLICKED (FEED/STORY)
  story?: { linkClicks?: number; replies?: number; tapsForward?: number; tapsBack?: number; exits?: number };
  totals?: { views?: number; likes?: number; comments?: number }; // total_* (inclui anúncio)
  insufficientData?: boolean; // (#10) < 5 de alcance ou vazio
}
export interface IgMediaRow extends IgMedia {
  insights: IgMediaInsights | null;
  ownClicks: number;
  keywordComments: number;
  privateReplies: number;
  replies: number;
  quizCompletes: number;
  frontSales: number;
  upsellSales: number;
  backendClosed: number;
  revenueNet: Cents;
  viral: boolean; // comentários/min acima do limiar de pico
}
export interface IgOverview {
  account: IgAccount;
  now: IgAccountSnapshot;
  followersSpark24h: number[];
  followersDelta24h: number;
  days: IgDailyStats[]; // últimos 30, com dataAsOf
  ownBio: { clicks24h: number; clicksSpark: number[]; toQuizRate: Rate | null; coverage: Rate | null }; // redirecionador
  realtime: {
    commentsLastHour: number;
    keywordLastHour: number;
    dmsInLastHour: number;
    mentionsLastHour: number;
  };
  demographics?: {
    at: DateKey;
    ageGender: { bucket: string; share: Rate }[];
    topCities: { name: string; share: Rate }[];
  };
  apiBudget: { callCountPct: Rate; cpuPct: Rate; timePct: Rate; regainInMin: number | null };
}
export interface IgCommentEvent {
  commentId: Id;
  parentId?: Id;
  mediaId: Id;
  productType: IgProductType;
  adId?: Id;
  fromIgsid: Id;
  fromUsername: string;
  text: string;
  receivedAt: Ms;
  isLive: boolean;
  matchedKeyword?: string;
  automationId?: Id;
  handled: 'queued' | 'skipped' | 'replied' | 'ignored';
  skipReason?: SkipReason;
}

// ============================================================================
// 6. ÁREA B · AUTOMAÇÕES
// ============================================================================
export type AutomationStatus = 'draft' | 'in_review' | 'live' | 'paused' | 'auto_paused' | 'archived';
export type MatchMode = 'exact_word' | 'contains' | 'any';
export type Trigger =
  | {
      kind: 'comment_keyword';
      scope: { type: 'all' } | { type: 'next_post' } | { type: 'media'; mediaIds: Id[] };
      keywords: string[];
      match: MatchMode;
    }
  | { kind: 'live_comment'; keywords: string[]; match: MatchMode }
  | { kind: 'dm_keyword'; keywords: string[]; match: MatchMode }
  | { kind: 'story_reply'; storyIds: Id[] | 'all'; keywords?: string[] }
  | { kind: 'story_mention' }
  | { kind: 'ad_click_to_direct'; adIds: Id[] | 'any' }
  | { kind: 'ice_breaker'; payload: string }
  | { kind: 'igme_ref'; ref: string };
export interface PublicReplyPolicy {
  enabled: boolean;
  poolId: Id;
  sampleRate: Rate;
  minGapPerMediaMs: number;
  noRepeatLastN: number;
}
export interface Automation {
  id: Id;
  name: string;
  slug: string; // utm_campaign=auto_<slug>
  status: AutomationStatus;
  pausedReason?: SafetyPauseReason;
  pausedAt?: Ms;
  triggers: Trigger[];
  publicReply: PublicReplyPolicy | null;
  flowId: Id;
  flowVersion: number;
  destination: 'quiz' | 'vsl'; // orgânico → quiz; anúncio → vsl
  safetyOverrides?: Partial<Pick<SafetyLimits, 'privateRepliesPerHour' | 'publicRepliesPerHour'>>;
  activeFrom?: Ms;
  activeUntil?: Ms;
  ownerId: Id;
  approvedBy?: Id;
  updatedBy: Id;
  updatedAt: Ms;
  warmup: boolean;
}
export interface QuickReply {
  title: string;
  payload: string;
  nextStepId: Id;
} // título ≤ 20 caracteres
export type HandoffReason =
  | 'unmatched_text'
  | 'price'
  | 'consulting'
  | 'talk_to_tiago'
  | 'negative'
  | 'ai_low_confidence'
  | 'manual';
export type Step =
  | { id: Id; kind: 'opening'; poolId: Id; quickReplies: QuickReply[] } // a private reply: sem link, com aviso
  | {
      id: Id;
      kind: 'message';
      poolId: Id;
      quickReplies?: QuickReply[];
      link?: { to: 'quiz' | 'vsl' };
      typing: boolean;
      gapMs?: number;
      nextStepId?: Id;
    } // gapMs: pausa fixa entre mensagens em sequência (≤ 3 000), só para a ordem chegar certa
  | { id: Id; kind: 'wait_reply'; timeoutMs: number; onReplyStepId: Id; onTimeoutStepId?: Id }
  | { id: Id; kind: 'branch'; rules: { when: Condition; stepId: Id }[]; elseStepId: Id }
  | { id: Id; kind: 'tag'; add?: Id[]; remove?: Id[]; nextStepId: Id }
  | {
      id: Id;
      kind: 'set_field';
      field: string;
      from: 'reply_text' | 'button_payload' | 'literal';
      value?: string;
      nextStepId: Id;
    }
  | {
      id: Id;
      kind: 'handoff';
      queue: 'support' | 'expert';
      reason: HandoffReason;
      priority: 'normal' | 'hot';
    }
  | { id: Id; kind: 'end' };
export type Condition =
  | { field: 'tag'; op: 'has' | 'not_has'; value: Id }
  | { field: 'button'; op: 'eq'; value: string }
  | {
      field: 'intent';
      op: 'eq';
      value: 'price' | 'consulting' | 'talk_to_tiago' | 'doubt' | 'negative' | 'optout';
    }
  | { field: 'stage'; op: 'eq' | 'gte'; value: ContactStage };
export interface PolicyIssue {
  stepId?: Id;
  code:
    | 'link_in_opening'
    | 'delay_over_30s'
    | 'no_disclosure'
    | 'follow_gate'
    | 'promise_language'
    | 'missing_handoff'
    | 'pool_too_small'
    | 'over_1000_bytes'
    | 'window_violation'
    | 'engagement_gate_no_alt'
    | 'human_agent_misuse';
  severity: 'block' | 'warn';
  message: string;
}
export interface Flow {
  id: Id;
  automationId: Id;
  version: number;
  status: 'draft' | 'approved' | 'live' | 'superseded';
  entryStepId: Id;
  steps: Record<Id, Step>;
  issues: PolicyIssue[];
  approvedBy?: Id;
  approvedAt?: Ms;
}

export type PoolPurpose =
  | 'public_reply'
  | 'opening'
  | 'link'
  | 'followup'
  | 'optout_ack'
  | 'handoff_ack'
  | 'disclosure';
export interface VariantPool {
  id: Id;
  name: string;
  purpose: PoolPurpose;
  minApproved: number;
  approvedCount: number;
  diversity: Rate;
}
export interface MessageVariant {
  id: Id;
  poolId: Id;
  text: string; // ≤ 1000 bytes UTF-8; placeholders {primeiro_nome} {link}
  origin: 'human' | 'ai_draft';
  status: 'draft' | 'approved' | 'retired';
  writtenBy: Id;
  approvedBy?: Id;
  approvedAt?: Ms;
  toneApprovedByExpert: boolean;
  maxSimilarity: Rate; // Jaccard de trigramas vs. pool; > 0,8 bloqueia
  lint: {
    rule: 'promise' | 'scarcity' | 'link_in_public' | 'personal_attribute_2p' | 'bytes' | 'emoji_excess';
    message: string;
  }[];
  stats: { sent: number; seen: number; replied: number; clicked: number; optOuts: number };
}
export type ContactStage =
  | 'engaged'
  | 'conversation'
  | 'link_clicked'
  | 'quiz_done'
  | 'vsl'
  | 'front'
  | 'upsell_20x'
  | 'consulting';
export interface Contact {
  igsid: Id;
  username: string;
  name?: string;
  profilePictureUrl?: string; // perfil só após consentimento
  consentAt?: Ms;
  firstSource: Trigger['kind'];
  firstMediaId?: Id;
  followsBusiness?: boolean; // leitura; nunca condição
  tags: Id[];
  fields: Record<string, string>;
  stage: ContactStage;
  buyerKey?: string;
  windowOpenUntil: Ms | null;
  humanWindowUntil: Ms | null;
  optOut: { at: Ms; via: 'keyword' | 'agent' | 'blocked' } | null;
  assignedTo?: Id;
  lastInboundAt?: Ms;
  /** Canal da conversa: ausente = Instagram (igsid e @username); 'whatsapp' = telefone em `phone`. */
  channel?: 'whatsapp';
  /** Telefone do WhatsApp (55 + DDD + número), quando `channel` é 'whatsapp'. */
  phone?: string;
}
export interface Tag {
  id: Id;
  name: string;
  colorSlot: 1 | 2 | 3 | 4 | 5 | 6;
  system: boolean;
  /** Etiqueta da equipe no modo ao vivo: sempre em cor neutra (padrão Apple). */
  neutral?: boolean;
  /** Conversas com esta etiqueta (quando a fonte conta). */
  count?: number;
}
export interface Conversation {
  id: Id;
  contact: Contact;
  mode: 'bot' | 'queued' | 'human' | 'closed';
  queue?: 'support' | 'expert';
  /** Responsável pelo lead (quem respondeu primeiro ou recebeu do dono/sócio). */
  assigneeId?: Id;
  /** Nome do responsável quando a fonte sabe (modo ao vivo). */
  assigneeName?: string;
  /** O responsável saiu da equipe: o lead não trava mais ninguém. */
  assigneeLeft?: boolean;
  priority: 'normal' | 'hot';
  handoff?: { at: Ms; reason: HandoffReason; fromStepId?: Id };
  slaDueAt?: Ms;
  windowState: 'open' | 'human_only' | 'closed';
  folder: 'primary' | 'requests' | 'general';
  automationId?: Id;
  lastMessageAt: Ms;
  lastPreview: string;
  unread: number;
  /** Desde quando a pessoa espera resposta de alguém (conversa com a equipe). Só a espera do cliente. */
  waitingSince?: Ms;
  /** Lembrete de retomar de quem está vendo: quando vence. */
  followUpAt?: Ms;
}
/** Resumo do lead em até 2 linhas (só a equipe vê), guardado até a pessoa escrever de novo. */
export interface LeadSummary {
  text: string;
  at: Ms;
  /** A pessoa escreveu depois do resumo: vale refazer. */
  stale: boolean;
}
/** Lembrete de retomar uma conversa (de cada pessoa; dia e hora de Brasília). */
export interface FollowUp {
  conversationId: Id;
  dueAt: Ms;
  note?: string;
}
export type FollowUpPreset = 'tomorrow' | '2d' | '1w';
export type FollowUpCommand =
  | { kind: 'set'; conversationId: Id; preset: FollowUpPreset; note?: string }
  | { kind: 'set'; conversationId: Id; date: DateKey; time?: string; note?: string }
  | { kind: 'clear'; conversationId: Id };
/** Anexo de uma mensagem (foto, vídeo, áudio ou arquivo), guardado no servidor da automação. */
export interface MessageMedia {
  id: Id;
  kind: 'image' | 'video' | 'audio' | 'file';
  /** Endereço no painel (com sessão); ausente enquanto baixa ou se não deu para baixar. */
  url?: string;
  status: 'ok' | 'pending' | 'failed';
  durationMs?: number;
  name?: string;
  mime?: string;
  bytes?: number;
}
export interface IgMessage {
  id: Id;
  conversationId: Id;
  direction: 'in' | 'out';
  at: Ms;
  kind:
    | 'text'
    | 'quick_reply'
    | 'postback'
    | 'story_reply'
    | 'story_mention'
    | 'media'
    | 'reaction'
    | 'private_reply';
  text?: string;
  variantId?: Id;
  sentBy?: { type: 'automation'; automationId: Id; stepId: Id } | { type: 'user'; userId: Id; role: Role };
  tag?: 'HUMAN_AGENT'; // só com sentBy.type === 'user'
  seenAt?: Ms;
  status: 'queued' | 'sent' | 'failed';
  internalNote?: boolean;
  /** Foto, vídeo, áudio ou arquivo da mensagem (kind 'media'). */
  media?: MessageMedia;
}
export interface SafetyLimits {
  privateRepliesPerHour: number; // 120 (teto oficial 750)
  publicRepliesPerHour: number; // 40
  publicMinGapPerMediaMs: number; // 20 000
  automatedDmPerHour: number; // 300
  automatedDmPerDay: number; // 1500
  perContactAutomatedPer24h: number; // 4
  privateReplyMinGapMs: number; // 30 000: espaçamento mínimo da fila (balde de fichas), não sorteado
  privateReplyMaxWaitMs: number; // 6 dias: passou disso, pula com expired_7d antes do prazo da Meta
  activeReplyTargetMs: number; // 5 000: alvo para responder a quem acabou de escrever (máx. 30 000 pela política)
  burst: { commentsPerMin: number; publicSampleRate: Rate }; // 30 / 0,2
  warmup: { active: boolean; startedAt: Ms; dailyMultipliers: Rate[] }; // 14 valores, 0,25 → 1
  autoPause: { failRate15m: Rate; optOutRate1h: Rate; webhookSilenceMs: number; tokenDaysLeft: number };
  optOutKeywords: string[];
  disclosureText: string;
  routing?: InboxRouting; // AUT-92 (ADR-015); ausente = padrão da V2 4.5
  setupChecks?: Partial<Record<IgSetupCheckId, { at: Ms; by: Id }>>; // AUT-93, itens conferidos à mão (ADR-015)
}
/** Roteamento das conversas passadas para humano (`AUT-92`, ADR-015). Gravado por `set_limits`. */
export interface InboxRouting {
  businessHours: { start: string; end: string }; // 'HH:MM' em São Paulo (padrão 09:00–21:00)
  slaMinutes: { expert: number; support: number }; // 15 / 60, só no horário de atendimento
  byReason: Partial<Record<HandoffReason, { queue: 'support' | 'expert'; priority: 'normal' | 'hot' }>>;
}
/** Itens do checklist do `AUT-93` que a API não confirma e a pessoa confere à mão (ADR-015). */
export type IgSetupCheckId = 'messages_access' | 'manychat_removed';
export type SafetyPauseReason =
  | 'fail_rate'
  | 'rate_limited'
  | 'permission'
  | 'optout_spike'
  | 'webhook_silent'
  | 'token_expiring'
  | 'manual_kill'
  | 'restriction_flagged';
export type SendKind = 'public_reply' | 'private_reply' | 'dm_auto' | 'dm_human';
export type SkipReason =
  | 'opt_out'
  | 'window_closed'
  | 'cap_hour'
  | 'cap_day'
  | 'cap_contact'
  | 'duplicate_comment'
  | 'paused'
  | 'conversation_human'
  | 'expired_7d'
  | 'sampled_out'
  | 'own_comment'
  | 'not_matched';
export interface SendLog {
  id: Id;
  kind: SendKind;
  queuedAt: Ms;
  scheduledFor: Ms;
  sentAt?: Ms;
  status: 'queued' | 'sent' | 'failed' | 'skipped';
  skipReason?: SkipReason;
  automationId?: Id;
  stepId?: Id;
  variantId?: Id;
  contactIgsid?: Id;
  commentId?: Id;
  mediaId?: Id;
  userId?: Id;
  apiError?: { code: number; subcode?: number; message: string };
  latencyMs?: number;
  utmContent?: string;
  /** Modo teste do servidor (modo ao vivo): passou pela fila, mas nada saiu para a Meta. */
  dryRun?: boolean;
  /** Motivo do servidor que não tem `SkipReason` equivalente (texto curto, modo ao vivo). */
  reasonText?: string;
}
export interface AccountHealth {
  at: Ms;
  score: number;
  band: 'good' | 'warn' | 'bad';
  usage: { privateRepliesHour: Rate; publicRepliesHour: Rate; dmHour: Rate; dmDay: Rate };
  failRate1h: Rate;
  optOutRate24h: Rate;
  variantDiversity: Rate;
  publicRepeatRate: Rate;
  requestsShare: Rate;
  webhookLagMs: number | null;
  tokenDaysLeft: number | null;
  permissionsOk: boolean;
  warmupDay: number | null;
  restriction: { flagged: boolean; since?: Ms; note?: string };
  pausedAutomations: Id[];
  killSwitch: { on: boolean; at?: Ms; by?: Id };
  factors: { id: string; label: string; impact: number; status: KpiStatus }[];
}
export interface AutomationMetrics {
  automationId: Id;
  window: { from: Ms; to: Ms };
  triggers: number;
  eligible: number;
  publicReplies: number;
  privateReplies: number;
  seen: number;
  replied: number;
  linkClicks: number;
  quizCompletes: number;
  vslPlays: number;
  frontSales: number;
  upsellSales: number;
  backendClosed: number;
  revenueNet: Cents;
  revenuePer100Comments: Cents | null;
  handoffs: number;
  optOuts: number;
  rates: { seen: Rate | null; reply: Rate | null; click: Rate | null; front: Rate | null };
  byVariant: {
    variantId: Id;
    sent: number;
    replyRate: Rate | null;
    ci90: [Rate, Rate] | null;
    comparable: boolean;
  }[];
  byMedia: { mediaId: Id; triggers: number; replied: number; frontSales: number }[];
  /**
   * Volumes que a fonte ainda não mede (modo ao vivo: leitura, clique, quiz, vendas). Vêm como 0 e
   * as telas escondem a etapa em vez de mostrar um zero que não é medido.
   */
  unavailable?: readonly (keyof AutomationMetrics)[];
}
export type AutomationCommand =
  | { kind: 'upsert'; automation: Omit<Automation, 'id' | 'updatedAt' | 'updatedBy'> & { id?: Id } }
  | { kind: 'upsert_flow'; flow: Omit<Flow, 'version' | 'issues'> }
  | { kind: 'submit_review'; automationId: Id }
  | { kind: 'publish'; automationId: Id } // exige automations:publish + step-up + 0 issues 'block'
  | { kind: 'pause'; automationId: Id; reason: 'manual' }
  | { kind: 'resume'; automationId: Id }
  | { kind: 'archive'; automationId: Id }
  | { kind: 'kill_switch'; on: boolean; note?: string }
  | { kind: 'flag_restriction'; on: boolean; note?: string }
  | { kind: 'set_limits'; patch: Partial<SafetyLimits> };
export type VariantCommand =
  | { kind: 'upsert'; variant: Pick<MessageVariant, 'poolId' | 'text'> & { id?: Id } }
  | { kind: 'approve'; id: Id }
  | { kind: 'retire'; id: Id }
  | { kind: 'tone_ok'; id: Id }
  | { kind: 'draft_ai'; poolId: Id; n: number }; // gera rascunhos 'ai_draft' (nunca vão ao ar sem aprovação)
/** Como terminou o atendimento humano (marcado ao resolver a conversa). */
export type AttendanceOutcome = 'sale' | 'consulting' | 'link' | 'no_interest' | 'no_reply' | 'other';
export type InboxCommand =
  | { kind: 'reply'; conversationId: Id; text: string; mediaId?: Id } // humano; com anexo, o arquivo sai antes do texto
  | { kind: 'note'; conversationId: Id; text: string }
  | { kind: 'assign'; conversationId: Id; userId: Id | null }
  | { kind: 'handoff'; conversationId: Id; queue: 'support' | 'expert'; priority: 'normal' | 'hot' }
  | { kind: 'return_to_bot'; conversationId: Id }
  | { kind: 'close'; conversationId: Id; outcome?: AttendanceOutcome; note?: string }
  | { kind: 'opt_out'; conversationId: Id }
  | { kind: 'tag'; conversationId: Id; add?: Id[]; remove?: Id[] };

/** Fila personalizada (filtro salvo da coluna Filas das Conversas). */
export interface InboxViewFilter {
  /** team = com a equipe agora; bot = com a automação; resolved = resolvida. Vazio = todas. */
  states?: ('team' | 'bot' | 'resolved')[];
  /** Só quem espera resposta (a última mensagem é da pessoa). */
  waitingReply?: boolean;
  /** 'me' (quem está vendo), 'none' (sem responsável) ou o id de alguém da equipe. */
  owner?: Id;
  /** Qualquer uma destas etiquetas. */
  tags?: Id[];
  queues?: ('support' | 'expert')[];
  /** comment = veio de comentário; direct = direct ou story. */
  origins?: ('comment' | 'direct')[];
}
export interface InboxView {
  id: Id;
  name: string;
  filter: InboxViewFilter;
  /** Conversas na fila agora, para quem está vendo (quando a fonte conta). */
  count?: number;
}
/** Quem atende (para nomes, "Passar para…" e o filtro por responsável). */
export interface InboxAgent {
  id: Id;
  name: string;
  role: Role;
  active: boolean;
}
export type InboxViewCommand =
  | { kind: 'create'; name: string; filter: InboxViewFilter }
  | { kind: 'update'; id: Id; name?: string; filter?: InboxViewFilter }
  | { kind: 'archive'; id: Id };
export type TagCommand =
  | { kind: 'create'; name: string }
  | { kind: 'rename'; id: Id; name: string }
  | { kind: 'archive'; id: Id };
export interface FlowTestResult {
  transcript: {
    at: number;
    from: 'contact' | 'bot';
    text: string;
    buttons?: string[];
    windowOpen: boolean;
  }[];
  issues: PolicyIssue[];
}

export type AutomationsTopic = 'ig.comments' | 'ig.dm' | 'ig.queue' | 'ig.metrics' | 'ig.health';
interface LiveBaseV2 {
  v: typeof CONTRACT_VERSION_V2;
  seq: number;
  ts: Ms;
}
export type IgLiveMessage = LiveBaseV2 &
  (
    | { type: 'ig.comment'; topic: 'ig.comments'; data: IgCommentEvent }
    | { type: 'ig.conversation'; topic: 'ig.dm'; data: Conversation }
    | { type: 'ig.message'; topic: 'ig.dm'; data: IgMessage }
    | { type: 'ig.send'; topic: 'ig.queue'; data: SendLog }
    | { type: 'ig.snapshot'; topic: 'ig.metrics'; data: IgAccountSnapshot }
    | { type: 'ig.media'; topic: 'ig.metrics'; data: IgMediaRow }
    | { type: 'ig.health'; topic: 'ig.health'; data: AccountHealth }
    | { type: 'ig.automation'; topic: 'ig.health'; data: Automation }
  );
export interface IgListQuery {
  search?: string;
  cursor?: string;
  limit: number;
  from?: Ms;
  to?: Ms;
  status?: string[];
  automationId?: Id;
  mediaId?: Id;
  queue?: 'support' | 'expert' | 'bot' | 'resolved' | 'mine';
  /** Fila personalizada (`InboxView.id`): o filtro roda na fonte. */
  view?: Id;
  /** Só o que está livre para quem vê: sem responsável (ativo) ou comigo. */
  free?: boolean;
}
export interface AutomationsProvider {
  readonly kind: 'mock' | 'api';
  getOverview(q: { from: Ms; to: Ms }): Promise<IgOverview>;
  listMedia(q: IgListQuery): Promise<Page<IgMediaRow>>;
  getMedia(mediaId: Id): Promise<IgMediaRow | null>;
  listAutomations(): Promise<{ automation: Automation; metrics7d: AutomationMetrics }[]>;
  getAutomation(id: Id): Promise<{ automation: Automation; flow: Flow; metrics: AutomationMetrics } | null>;
  automation(cmd: AutomationCommand): Promise<Automation>;
  testFlow(flowId: Id, script: { text?: string; button?: string }[]): Promise<FlowTestResult>; // nunca envia
  testMatch(text: string): Promise<{ automationId: Id | null; keyword?: string; skipReason?: SkipReason }>;
  listPools(): Promise<VariantPool[]>;
  listVariants(poolId: Id): Promise<MessageVariant[]>;
  variant(cmd: VariantCommand): Promise<MessageVariant[]>;
  listConversations(q: IgListQuery): Promise<Page<Conversation>>;
  getConversation(id: Id): Promise<{
    conversation: Conversation;
    messages: IgMessage[];
    journey: { at: Ms; label: string }[];
  } | null>;
  inbox(cmd: InboxCommand): Promise<Conversation>;
  revealContact?(igsid: Id): Promise<{ name: string; username: string }>; // audita
  listContacts(
    q: IgListQuery & { tag?: Id; stage?: ContactStage; optedOut?: boolean },
  ): Promise<Page<Contact>>;
  listTags(): Promise<Tag[]>;
  /** Etiquetas da equipe (criar, renomear, arquivar). Ausente = a fonte não oferece. */
  tagCommand?(cmd: TagCommand): Promise<Tag[]>;
  /** Filas personalizadas e quem atende. Ausentes = a fonte não oferece. */
  listViews?(): Promise<InboxView[]>;
  viewCommand?(cmd: InboxViewCommand): Promise<InboxView[]>;
  listAgents?(): Promise<InboxAgent[]>;
  /** Rascunho da próxima resposta, na voz do Tiago (nunca envia). Ausente = a fonte não oferece. */
  suggestReply?(conversationId: Id): Promise<{ text: string }>;
  /** Resumo do lead: `generate` faz quando falta ou está velho; `refresh` refaz sempre. */
  leadSummary?(
    conversationId: Id,
    opts?: { generate?: boolean; refresh?: boolean },
  ): Promise<LeadSummary | null>;
  /** Lembretes de retomar de quem está vendo. */
  listFollowUps?(): Promise<FollowUp[]>;
  followUp?(cmd: FollowUpCommand): Promise<void>;
  getHealth(): Promise<{
    health: AccountHealth;
    limits: SafetyLimits;
    connection: IgAccount;
    history24h: AccountHealth[];
  }>;
  listSendLog(q: IgListQuery & { kind?: SendKind[]; skipReason?: SkipReason[] }): Promise<Page<SendLog>>;
  listComments(q: IgListQuery): Promise<Page<IgCommentEvent>>;
  subscribe(
    topics: AutomationsTopic[],
    onMessage: (m: IgLiveMessage) => void,
    opts?: { lastSeq?: number },
  ): Unsubscribe;
}

// ============================================================================
// 7. ÁREA C · ESTÚDIO
// ============================================================================
export type Sec = number;
export interface StudioMember {
  userId: Id;
  name: string;
  initials: string;
  role: Role;
  functions: StudioFunction[];
}
export interface PainScene {
  id: Id;
  text: string;
  source: 'study' | 'comments' | 'quiz' | 'support' | 'team';
}
export interface Persona {
  id: Id;
  code: string;
  name: string;
  lifeMoment: string;
  painScenes: PainScene[];
  seedHook?: string;
  kind: 'demographic' | 'situation' | 'geographic';
  status: 'active' | 'saturating' | 'paused';
  notes?: string;
}
export interface CuriosityElement {
  id: Id;
  code: string;
  text: string;
  source: 'vsl_bullet' | 'vsl_lead' | 'expansion';
  needsValidation: boolean;
  truthStatus: 'pending' | 'confirmed' | 'rejected';
}
export interface SliceCell {
  personaId: Id;
  elementId: Id;
  produced: number;
  tested: number;
  winners: number;
  frontSales: number;
  upsellSales: number;
  backendSales: number;
  bestHookRate: Rate | null;
  lastTestedAt: Ms | null;
  saturating: boolean;
}
export type HookType =
  | 'polemica'
  | 'crenca_limitante'
  | 'humor'
  | 'numero'
  | 'cena'
  | 'formato_viral'
  | 'promessa_controle'
  | 'contraintuitivo'
  | 'inimigo'
  | 'prova'
  | 'identificacao'
  | 'stop_scroll_visual';
export interface ScriptLine {
  text: string;
  estSec: Sec;
  bannerText?: string;
  bRoll?: string;
  onScreen?: string;
}
export interface HookStats {
  creatives: number;
  impressions: number;
  hookRate: Rate | null;
  hookRateCi90: [Rate, Rate] | null;
  playRate: Rate | null;
  frontSales: number;
  bestRoasFunnel: number | null;
  bankEligible: boolean;
}
export interface Hook {
  id: Id;
  code: string;
  type: HookType;
  personaId: Id;
  elementId?: Id;
  spoken: ScriptLine;
  bridge?: ScriptLine;
  visualOpening?: string;
  socialIssueRisk: boolean;
  status: 'draft' | 'approved' | 'recorded' | 'tested' | 'banked' | 'retired';
  stats: HookStats;
  origin: { kind: 'new' | 'from_bank' | 'from_organic' | 'from_reference'; refId?: Id };
  stackedOn: { adName: string; result: 'pending' | 'won' | 'lost' }[];
  createdBy: Id;
  createdAt: Ms;
}
export type BodyBlockRole =
  | 'interest'
  | 'desire'
  | 'mechanism_teaser'
  | 'future_pacing'
  | 'proof'
  | 'cta1'
  | 'curiosity'
  | 'recovery'
  | 'cta2';
export interface BodyBlock extends ScriptLine {
  role: BodyBlockRole;
  cvi?: { curiosity?: string; value?: string; indirect?: string };
  lookAt?: 'interviewer' | 'camera';
}
export type CreativeFormat =
  | 'podcast'
  | 'selfie'
  | 'narrado'
  | 'caixinha'
  | 'dialogo'
  | 'tela_dividida'
  | 'ugc'
  | 'entrevista_rua'
  | 'corte_vsl'
  | 'vlog';
export interface Body {
  id: Id;
  code: string;
  title: string;
  audience: string;
  format: CreativeFormat;
  targetSec: Sec;
  blocks: BodyBlock[];
  hookIds: Id[];
  sourceAssetId?: Id;
}
export type ScriptKind =
  | 'ad_body'
  | 'organic_reel'
  | 'carousel'
  | 'story_sequence'
  | 'live'
  | 'vsl_lead'
  | 'upsell_video';
export interface Claim {
  id: Id;
  text: string;
  blockRef: string;
  status: 'pending' | 'confirmed' | 'edited' | 'removed';
  note?: string;
}
export type ComplianceRuleId =
  | 'no_result_promise'
  | 'personal_attribute_2p'
  | 'social_issue'
  | 'blackout_window'
  | 'testimonial_authorized'
  | 'no_third_party_media'
  | 'no_job_offer_language'
  | 'no_product_before_offer'
  | 'true_anchors_only'
  | 'not_legal_advice'
  | 'hook_length'
  | 'no_false_scarcity'
  | 'no_engagement_gate';
export interface ComplianceCheck {
  rule: ComplianceRuleId;
  status: 'pass' | 'warn' | 'fail' | 'manual';
  source: 'lint' | 'human';
  match?: string;
  at?: string;
  message: string;
}
export interface Script {
  id: Id;
  contentItemId: Id;
  kind: ScriptKind;
  version: number;
  bodyId?: Id;
  slides?: { n: number; text: string; visual?: string }[];
  lines?: ScriptLine[];
  caption?: string;
  firstComment?: string;
  keyword?: string; // palavra-chave da Área B ligada ao post
  claims: Claim[];
  lint: ComplianceCheck[];
  estTotalSec: Sec;
  updatedBy: Id;
  updatedAt: Ms;
}

export type ContentChannel = 'organic' | 'paid';
export type OrganicPlacement = 'reel' | 'feed' | 'carousel' | 'story' | 'live' | 'trial_reel';
export type ContentKind = 'ad_body' | 'ad_creative' | 'ad_variation' | 'organic' | 'vsl_lead' | 'study_task';
export type ContentStage =
  | 'idea'
  | 'scripting'
  | 'approval'
  | 'ready_to_record'
  | 'recorded'
  | 'editing'
  | 'review'
  | 'ready'
  | 'live'
  | 'reading'
  | 'decided'
  | 'archived';
export const STAGE_FLOW: Record<ContentStage, readonly ContentStage[]> = {
  idea: ['scripting', 'archived'],
  scripting: ['approval', 'idea', 'archived'],
  approval: ['ready_to_record', 'scripting'],
  ready_to_record: ['recorded', 'scripting'],
  recorded: ['editing'],
  editing: ['review'],
  review: ['ready', 'editing'],
  ready: ['live', 'editing'],
  live: ['reading'],
  reading: ['decided'],
  decided: ['archived', 'editing'],
  archived: [],
};
export type BlockReason =
  | 'awaiting_authorization'
  | 'awaiting_truth'
  | 'blackout'
  | 'missing_asset'
  | 'other';
export interface ContentItem {
  id: Id;
  code: string;
  kind: ContentKind;
  channel: ContentChannel;
  title: string;
  stage: ContentStage;
  stageSince: Ms;
  blocked?: { reason: BlockReason; note: string; until?: Ms };
  parentId?: Id;
  batchId?: Id;
  scriptId?: Id;
  assembly?: { hookId: Id; bodyId: Id; stackedOnHookId?: Id; stopScrollAssetId?: Id };
  variationOf?: { kind: 'post_text' | 'title' | 'banner' | 'hook_stack' | 'edit' | 'stop_scroll' | 'format' };
  placement?: OrganicPlacement;
  format?: CreativeFormat;
  personaIds: Id[];
  elementIds: Id[];
  hookType?: HookType;
  adName?: string; // = utm_content
  owners: Partial<Record<StudioFunction, Id>>;
  due?: { stage: ContentStage; at: Ms };
  approvals: {
    truth: 'none' | 'pending' | 'approved' | 'stale';
    compliance: 'none' | 'pending' | 'approved' | 'stale';
    finalCut: 'none' | 'pending' | 'approved';
  };
  recordingSessionId?: Id;
  assetVersionIds: Id[];
  links: {
    igMediaId?: Id;
    metaAdIds?: Id[];
    rotaDecisionIds?: Id[];
    referenceIds?: Id[];
    studyPointIds?: Id[];
    automationId?: Id;
  };
  tags: string[];
  createdBy: Id;
  createdAt: Ms;
  updatedAt: Ms;
}
export interface Batch {
  id: Id;
  code: string;
  weekOf: DateKey;
  target: { bodies: number; hooksPerBody: number; minPersonas: number; validatedShare: Rate };
  bodyIds: Id[];
  creativeItemIds: Id[];
  variationItemIds: Id[];
  status: 'planning' | 'scripting' | 'recording' | 'editing' | 'uploading' | 'testing' | 'read' | 'closed';
  test: { dailyBudget: Cents; days: number; structure: '1-3-1' };
  launchAt?: Ms;
  readAt?: Ms;
  health: {
    creatives: number;
    personasCovered: number;
    validatedShare: Rate;
    approvedScripts: number;
    recorded: number;
    edited: number;
    uploaded: number;
    blockers: { itemId: Id; reason: BlockReason }[];
  };
  testSpend: Cents;
  winners: number;
  costPerWinner: Cents | null;
}
export type RecordingSetup = 'podcast_2cam' | 'selfie' | 'street' | 'ugc' | 'screen' | 'vlog';
export interface Take {
  n: number;
  at: Ms;
  mark: 'good' | 'maybe' | 'bad';
  note?: string;
}
export interface RecordingSlot {
  id: Id;
  contentItemId: Id;
  block: {
    kind: 'hook' | 'bridge' | 'body' | 'cta1' | 'cta2' | 'organic' | 'stop_scroll';
    refId: Id;
    label: string;
  };
  text: string;
  estSec: Sec;
  lookAt?: 'interviewer' | 'camera';
  bannerText?: string;
  editorCue?: string;
  takesPlanned: number;
  takes: Take[];
  status: 'todo' | 'done' | 'skipped';
}
export interface RecordingSession {
  id: Id;
  date: DateKey;
  start: Ms;
  end: Ms;
  setup: RecordingSetup;
  location: string;
  checklist: { id: Id; label: string; done: boolean }[];
  slots: RecordingSlot[];
  attendees: Id[];
  status: 'planned' | 'in_progress' | 'done' | 'cancelled';
  estMinutes: number;
}
export interface QcResult {
  rule:
    | 'res_1080x1920'
    | 'fps_30'
    | 'duration_range'
    | 'hook_len'
    | 'loudness'
    | 'true_peak'
    | 'safe_zone_manual';
  status: 'pass' | 'warn' | 'fail';
  detail?: string;
}
export interface AssetVersion {
  id: Id;
  contentItemId: Id;
  v: number;
  kind: 'raw' | 'cut' | 'final' | 'thumb' | 'banner';
  storageKey: string;
  posterUrl: string | null;
  bytes: number;
  uploadedBy: Id;
  uploadedAt: Ms;
  media?: {
    durationSec: Sec;
    width: number;
    height: number;
    fps: number;
    loudnessLufs?: number;
    truePeakDb?: number;
  };
  qc: QcResult[];
}
export interface ReviewComment {
  id: Id;
  assetVersionId: Id;
  atMs: number;
  rangeEndMs?: number;
  text: string;
  by: Id;
  at: Ms;
  resolved: boolean;
  category?: 'edicao' | 'legenda' | 'banner' | 'audio' | 'compliance' | 'copy';
}
export type ApprovalGate = 'truth' | 'compliance' | 'final_cut' | 'publish';
export interface Approval {
  id: Id;
  gate: ApprovalGate;
  subject: { type: 'script' | 'hook' | 'asset_version' | 'content_item'; id: Id; version: number };
  requiredFunction: StudioFunction;
  status: 'pending' | 'approved' | 'changes_requested' | 'rejected' | 'stale';
  checklist: ComplianceCheck[];
  decidedBy?: Id;
  decidedAt?: Ms;
  comment?: string;
  voiceNoteRef?: string;
  requestedBy: Id;
  requestedAt: Ms;
  dueAt?: Ms;
}
export type CalendarKind =
  | 'organic_post'
  | 'story'
  | 'live'
  | 'batch_launch'
  | 'batch_read'
  | 'recording'
  | 'weekly_review'
  | 'blackout'
  | 'deadline'
  | 'class';
export interface CalendarEvent {
  id: Id;
  kind: CalendarKind;
  title: string;
  start: Ms;
  end?: Ms;
  allDay?: boolean;
  contentItemId?: Id;
  batchId?: Id;
  recordingSessionId?: Id;
  publish?: {
    mode: 'api_scheduled' | 'manual_reminder';
    state: 'scheduled' | 'publishing' | 'published' | 'failed' | 'reminder_sent';
    error?: string;
  };
  blackout?: {
    blocks: { hookTypes?: HookType[]; rules?: ComplianceRuleId[]; tags?: string[] };
    reason: string;
  };
  ownerId?: Id;
}
export interface StudyPoint {
  id: Id;
  studyId: Id;
  chapterId?: Id;
  at?: Sec;
  atEnd?: Sec;
  text: string;
  why?: string;
  howToApply?: string;
  furoId?: FuroId;
  tags: string[];
  appliedIn: { type: 'content_item' | 'batch' | 'hook' | 'rule' | 'plan_task'; id: Id }[];
  status: 'to_apply' | 'applied' | 'not_applicable';
}
export interface Study {
  id: Id;
  title: string;
  source: {
    type: 'podcast' | 'youtube' | 'course' | 'internal_doc' | 'book' | 'call';
    url?: string;
    authors: string[];
    episode?: string;
  };
  durationSec?: Sec;
  addedBy: Id;
  addedAt: Ms;
  chapters: { id: Id; at: Sec; title: string }[];
  pointsCount: number;
  appliedCount: number;
}
export interface Reference {
  id: Id;
  url: string;
  platform: 'instagram' | 'youtube' | 'tiktok' | 'meta_ad_library' | 'site' | 'other';
  author?: string;
  kind: 'organic' | 'ad' | 'vsl' | 'upsell' | 'other';
  format?: CreativeFormat;
  hookType?: HookType;
  whyItWorks: string;
  timestamps: { at: Sec; note: string }[];
  rights: 'link_only' | 'own' | 'authorized';
  publicCounts?: { likes?: number; comments?: number; followers?: number; fetchedAt: Ms };
  boardIds: Id[];
  tags: string[];
  savedBy: Id;
  savedAt: Ms;
  usedIn: Id[];
}
export interface Board {
  id: Id;
  name: string;
  description?: string;
  referenceIds: Id[];
}
export interface PublicVoiceSnippet {
  id: Id;
  text: string;
  source: 'ig_comment' | 'ig_dm' | 'quiz' | 'support_tag';
  personaId?: Id;
  sentiment?: 'dor' | 'desejo' | 'objecao' | 'piada';
  at: Ms;
  count: number;
}
export type ContentAttributionMethod = 'utm_content' | 'dm_link_utm' | 'story_link_utm' | 'bio_utm' | 'none';
export interface ContentPerformance {
  contentItemId: Id;
  code: string;
  title: string;
  channel: ContentChannel;
  window: { from: Ms; to: Ms };
  tags: { hookType?: HookType; format?: CreativeFormat; personaCodes: string[]; openingImage?: string };
  organic?: {
    views: number | null;
    reach: number | null;
    likes: number;
    comments: number;
    shares: number | null;
    saves: number | null;
    follows: number | null;
    profileVisits: number | null;
    avgWatchSec: Sec | null;
    skipRate3s: Rate | null;
    keywordTriggers: number;
  };
  paid?: {
    spend: Cents;
    impressions: number;
    playRate: Rate | null;
    hookRate: Rate | null;
    holdRate: Rate | null;
    ctrLink: Rate | null;
    cpcLink: Cents | null;
    pitchReached: number;
    frontCpa: Cents | null;
  };
  funnel: {
    quizStarts: number;
    vslPlays: number;
    frontSales: number;
    upsellSales: number;
    backendSales: number;
    revenueNet: Cents;
    buyerValueNet: Cents | null;
    roasFunnel: number | null;
  };
  attribution: { method: ContentAttributionMethod; coverage: Rate };
  freshness: SuiteFreshness[];
}
export type CreativeVerdict =
  | 'extend_1d'
  | 'scale'
  | 'pre_scale'
  | 'kill'
  | 'bank_hook'
  | 'lateralize'
  | 'sleep'
  | 'retire'
  | 'repurpose_as_ad';
export interface CreativeDecision {
  id: Id;
  contentItemId: Id;
  verdict: CreativeVerdict;
  basis: { frontSales: number; days: number; hookRate: Rate | null; roasFunnel: number | null; rule: string };
  rotaDecisionId?: Id;
  ruleId?: RuleId;
  spawnedItemIds: Id[];
  decidedBy: Id | 'system';
  at: Ms;
}
export interface WeeklyReview {
  id: Id;
  weekOf: DateKey;
  attendees: Id[];
  numbers: { label: string; value: string; status: KpiStatus; target?: string }[]; // vindos do motor do Painel
  creatives: ContentPerformance[];
  lists: { reuseHooks: Id[]; lateralize: Id[]; turnOff: Id[] };
  learnings: {
    kind: 'opening_image' | 'persona' | 'element' | 'scene' | 'format';
    text: string;
    evidenceIds: Id[];
  }[];
  furoChanges: { id: FuroId; to: Furo['status'] }[];
  generatedItemIds: Id[];
  briefingId?: Id;
  closedAt?: Ms;
}
export interface StudioParams {
  hookRateBankCut: Rate;
  hookRateSwapBelow: Rate;
  hookRateWinner: Rate;
  minImpressionsForHookVerdict: number;
  speechWordsPerSec: number;
  hookMaxSec: Sec;
  hookTargetSec: [Sec, Sec];
  batch: Batch['target'];
  organicCadence: Partial<Record<OrganicPlacement, number>>;
  wipLimits: Partial<Record<ContentStage, number>>;
  blackouts: {
    from: DateKey;
    to: DateKey;
    reason: string;
    blocks: NonNullable<CalendarEvent['blackout']>['blocks'];
  }[];
}
export interface StudioToday {
  // EST-01
  for: Id;
  nextRecording: RecordingSession | null;
  approvalsWaiting: Approval[];
  blockersOwned: ContentItem[];
  yesterdayPost: { contentItemId: Id; sentence: string } | null;
  dueToday: ContentItem[];
  weekSentence: string;
}
export type StudioListKind =
  | 'items'
  | 'hooks'
  | 'bodies'
  | 'personas'
  | 'elements'
  | 'batches'
  | 'sessions'
  | 'events'
  | 'approvals'
  | 'studies'
  | 'points'
  | 'references'
  | 'boards'
  | 'voice'
  | 'performance'
  | 'comments'
  | 'archive';
/** EST-104 Acervo: o que já foi gravado, com o take e o uso. */
export interface ArchiveRow {
  contentItemId: Id;
  code: string;
  title: string;
  sessionId: Id | null;
  recordedOn: DateKey | null;
  slot: RecordingSlot['block'] | null;
  goodTakes: number;
  usedIn: Id[];
  tested: boolean;
  published: boolean;
  asset: AssetVersion | null;
}
export interface StudioRowMap {
  items: ContentItem;
  hooks: Hook;
  bodies: Body;
  personas: Persona;
  elements: CuriosityElement;
  batches: Batch;
  sessions: RecordingSession;
  events: CalendarEvent;
  approvals: Approval;
  studies: Study;
  points: StudyPoint;
  references: Reference;
  boards: Board;
  voice: PublicVoiceSnippet;
  performance: ContentPerformance;
  comments: ReviewComment;
  archive: ArchiveRow;
}
export interface StudioListQuery {
  search?: string;
  cursor?: string;
  limit: number;
  from?: Ms;
  to?: Ms;
  stage?: ContentStage[];
  channel?: ContentChannel;
  ownerId?: Id | 'me';
  batchId?: Id;
  studyId?: Id;
  personaId?: Id;
  status?: string[];
  tag?: string;
}
export type StudioCommand =
  | { kind: 'upsert_item'; item: Partial<ContentItem> & Pick<ContentItem, 'kind' | 'channel' | 'title'> }
  | { kind: 'move'; itemId: Id; to: ContentStage } // valida STAGE_FLOW e portões
  | { kind: 'block'; itemId: Id; reason: BlockReason; note: string }
  | { kind: 'unblock'; itemId: Id }
  | { kind: 'save_script'; script: Omit<Script, 'version' | 'lint' | 'updatedAt' | 'updatedBy'> }
  | { kind: 'upsert_hook'; hook: Partial<Hook> & Pick<Hook, 'type' | 'personaId' | 'spoken'> }
  | {
      kind: 'decide_approval';
      approvalId: Id;
      status: 'approved' | 'changes_requested' | 'rejected';
      comment?: string;
    }
  | { kind: 'claim'; claimId: Id; status: Claim['status']; note?: string }
  | { kind: 'mark_take'; sessionId: Id; slotId: Id; mark: Take['mark']; note?: string }
  | { kind: 'schedule'; event: Omit<CalendarEvent, 'id'> & { id?: Id } }
  | { kind: 'comment'; comment: Omit<ReviewComment, 'id' | 'at' | 'by' | 'resolved'> }
  | { kind: 'resolve_comment'; id: Id }
  | { kind: 'decide_creative'; contentItemId: Id; verdict: CreativeVerdict }
  | { kind: 'stack_hook'; hookId: Id; onAdName: string }
  | { kind: 'point_status'; pointId: Id; status: StudyPoint['status']; applyAs?: ContentKind }
  | { kind: 'save_reference'; reference: Omit<Reference, 'id' | 'savedAt' | 'savedBy' | 'usedIn'> }
  | { kind: 'close_review'; reviewId: Id; lists: WeeklyReview['lists']; learnings: WeeklyReview['learnings'] }
  | { kind: 'set_params'; patch: Partial<StudioParams> };
export type StudioTopic = 'studio.items' | 'studio.approvals' | 'studio.recording';
export type StudioLiveMessage = LiveBaseV2 &
  (
    | { type: 'studio.item'; topic: 'studio.items'; data: ContentItem }
    | { type: 'studio.approval'; topic: 'studio.approvals'; data: Approval }
    | { type: 'studio.take'; topic: 'studio.recording'; data: { sessionId: Id; slotId: Id; take: Take } }
  );
export interface StudioProvider {
  readonly kind: 'mock' | 'api';
  getToday(): Promise<StudioToday>;
  list<K extends StudioListKind>(kind: K, q: StudioListQuery): Promise<Page<StudioRowMap[K]>>;
  getItem(id: Id): Promise<{
    item: ContentItem;
    script: Script | null;
    approvals: Approval[];
    assets: AssetVersion[];
    performance: ContentPerformance | null;
    decisions: CreativeDecision[];
  } | null>;
  getBatch(id: Id): Promise<{ batch: Batch; bodies: Body[]; hooks: Hook[]; items: ContentItem[] } | null>;
  getSession(id: Id): Promise<RecordingSession | null>;
  getMatrix(): Promise<{ personas: Persona[]; elements: CuriosityElement[]; cells: SliceCell[] }>;
  getStudy(id: Id): Promise<{ study: Study; points: StudyPoint[] } | null>;
  getWeeklyReview(weekOf?: DateKey): Promise<WeeklyReview>;
  getParams(): Promise<StudioParams>;
  members(): Promise<StudioMember[]>;
  command(cmd: StudioCommand): Promise<{ ok: true; ids: Id[] }>;
  subscribe(
    topics: StudioTopic[],
    onMessage: (m: StudioLiveMessage) => void,
    opts?: { lastSeq?: number },
  ): Unsubscribe;
}

// ============================================================================
// 8. SUÍTE: provider raiz, resumo do lançador, notificações
// ============================================================================
export interface AreaSummary {
  area: Area;
  line: string; // "R$ 412 hoje · 1 alerta"
  badge: { count: number; tone: 'neutral' | 'warning' | 'critical' } | null;
  lastRoute?: string;
}
export interface SuiteNotification {
  id: Id;
  at: Ms;
  area: Area;
  severity: Severity | 'info';
  title: string;
  route: string;
  read: boolean;
}
export interface SuiteProvider {
  auth: AuthProvider;
  painel(): Promise<PainelProviderV2>;
  automacoes(): Promise<AutomationsProvider>;
  estudio(): Promise<StudioProvider>;
  summary(): Promise<AreaSummary[]>; // uma chamada, < 50 ms no mock
  notifications(onMessage: (n: SuiteNotification) => void): Unsubscribe;
  markRead(ids: Id[]): Promise<void>;
}

// ============================================================================
// 9. SIMULAÇÃO V2
// ============================================================================
export type ScenarioIdV2 =
  | ScenarioId
  | 'realista'
  | 'realista_rastreado'
  | 'teste_131'
  | 'upsell_v2'
  | 'backend_pipeline'
  | 'organico_viral'
  | 'fatia_saturada'
  | 'automacao_pausada'
  | 'token_expirando'
  | 'fila_humana_cheia'
  | 'restricao_conta';
export interface SimStateV2 extends Omit<SimState, 'scenario'> {
  scenario: ScenarioIdV2;
  igSeedOffset?: number;
}
export interface SimControlV2 {
  get(): Promise<SimStateV2>;
  set(patch: Partial<Omit<SimStateV2, 'simNow'>>): Promise<SimStateV2>;
  resetDay(): Promise<void>;
}

// ============================================================================
// 10. CAMPOS OPCIONAIS EM INTERFACES DA V1 (module augmentation; nada é obrigatório)
// ============================================================================
declare module './index.ts' {
  interface Order {
    channel?: SalesChannel;
    productV2?: SuiteProductKind;
    clickId?: Id | null;
  }
  interface InsightRow {
    testPhase?: TestPhase;
    nameParts?: AdNameParts;
    adPlayRate?: Rate | null;
    vslMinute1?: Rate | null;
    pitchConversion?: Rate | null;
    costPerVisit?: Cents | null;
    costPerPitch?: Cents | null;
    roasFunnel?: number | null;
    roasFunnelBackend?: number | null;
    /** Visão "Gerenciador" (como no Gerenciador de Anúncios): resultados = compras que a Meta atribui. */
    results?: number;
    costPerResult?: Cents | null;
    /** Valor das compras pelo preço do produto (o mesmo da compra enviada à Meta). */
    resultValue?: Cents;
    roasValue?: number | null;
    costPerLpv?: Cents | null;
    /** Vendas reais da Kiwify ligadas ao anúncio pelo link (curso + 20X) e o valor delas. */
    kiwifySales?: number;
    kiwifyValue?: Cents;
    /** Valor das compras como a Meta registrou (antes da correção para o preço cheio). */
    metaValueRaw?: Cents;
    /** Vendas da Kiwify pelo link separadas em curso e 20X (referência; vendas de anúncio contam pela Meta). */
    kiwifyFront?: number;
    kiwifyUpsell?: number;
    /** Nomes do conjunto e da campanha da linha (níveis abaixo da campanha). */
    adsetName?: string | null;
    campaignName?: string | null;
    /** Primeiro dia com gasto (idade real do anúncio). */
    firstSpendDate?: DateKey | null;
    backendClosed?: number;
    ltvPerBuyer?: Cents | null;
    window7dDelta?: number | null;
  }
  interface QuizQuestion {
    multiple?: boolean;
    condition?: string;
    channel?: 'organic';
  }
  interface Settings {
    economicsV2?: EconomicsV2;
    studio?: StudioParams;
    /** Ajustes › Backend: meta de volume das consultorias (topo item 4; ADR-017). Ausente = 3/dia, 90/mês. */
    consultingCapacity?: ConsultingCapacitySetting;
    /** Ajustes › Backend: prazos e probabilidades `[E]` do pipeline (V2 3.4; ADR-017). Ausente = padrão. */
    backendPipeline?: BackendPipelineSetting;
  }
  interface DashboardSnapshot {
    kpisV2?: Partial<Record<KpiIdV2, KpiValueV2>>;
    /**
     * Compras que a Meta atribui aos anúncios no período (Gerenciador: pixel + API de Conversões),
     * curso e 20X. Conta também quem viu ou clicou no anúncio e comprou por outro caminho (bio,
     * direct), por isso passa das vendas com UTM de anúncio. Ausente sem a conta da Meta ligada.
     */
    adsMeta?: { purchases: number; valueCents: number } | null;
    channel?: ChannelView;
    phaseGoals?: PlanGoal[];
    backend?: { open: number; valueAtStake: Cents; nextDueAt: Ms | null };
  }
  interface BootPayload {
    kpiDefsV2?: Partial<Record<KpiIdV2, KpiDefV2>>;
    session?: SessionInfo;
  }
  interface Briefing {
    reviewId?: Id;
  }
}
/** Briefing do tipo "Revisão de segunda": `kind: 'weekly'` + `reviewId` (sem nova união). */
export type ReviewBriefing = Briefing & { kind: 'weekly'; reviewId: Id };
